import clsx from "clsx";
import { memo, useState } from "react";
import { useTranslation } from "@pancakeswap/localization";
import { escapeRegExp } from "@pancakeswap/utils/escapeRegExp";
import { groupDigits as group } from "@pancakeswap/utils/groupDigits";
import { inputVariants, InputVariants } from "./SwapWidget.css";

const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`); // match escaped "." characters via in a non-capturing group

export type NumericalInputProps = {
  value: string | number;
  onUserInput: (input: string) => void;
  fontSize?: string;
  /**
   * Thousands separators while the field is not being edited (350,085,774); the raw number as soon as it is focused, so typing
   * never meets a comma (site review 59). The value itself, and what onUserInput receives, stay raw.
   */
  groupDigits?: boolean;
} & InputVariants &
  Omit<React.HTMLProps<HTMLInputElement>, "ref" | "onChange" | "as">;

export const NumericalInput = memo(function InnerInput({
  value,
  onUserInput,
  placeholder,
  error,
  align,
  className,
  loading,
  groupDigits,
  onFocus,
  onBlur,
  ...rest
}: NumericalInputProps) {
  const [editing, setEditing] = useState(false);
  const enforcer = (nextUserInput: string) => {
    if (nextUserInput === "" || inputRegex.test(escapeRegExp(nextUserInput))) {
      onUserInput(nextUserInput);
    }
  };

  const { t } = useTranslation();

  return (
    <input
      className={clsx(
        className,
        inputVariants({
          error,
          align,
          loading,
        })
      )}
      {...rest}
      value={groupDigits && !editing ? group(value) : value}
      onFocus={(event) => {
        setEditing(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setEditing(false);
        onBlur?.(event);
      }}
      onChange={(event) => {
        // replace commas with periods, because we exclusively uses period as the decimal separator
        enforcer(event.target.value.replace(/,/g, "."));
      }}
      // universal input options
      inputMode="decimal"
      title={t("Token Amount")}
      autoComplete="off"
      autoCorrect="off"
      // text-specific options
      type="text"
      pattern={groupDigits && !editing ? "^[0-9,]*[.]?[0-9]*$" : "^[0-9]*[.,]?[0-9]*$"}
      placeholder={placeholder || "0.0"}
      minLength={1}
      maxLength={79}
      spellCheck="false"
    />
  );
});
